import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import smsService from '../../services/smsService';
import {
  MagnifyingGlass, 
  FunnelSimple, 
  ArrowsClockwise,
  Phone,
  Smiley,
  WarningCircle,
  CheckCircle,
  Clock,
  Download,
  Eye
} from '@phosphor-icons/react';
import './SMSDashboardPage.css';

function SMSDashboardPage() {
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const limit = 20;

  // Load SMS notifications
  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await smsService.listNotifications({
        status_filter: statusFilter,
        notification_type: typeFilter,
        limit,
        page
      });
      setNotifications(data.notifications || []);
      setTotalCount(data.total || 0);
    } catch (err) {
      toast.error('Failed to load SMS notifications');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, page, limit]);

  // Load statistics
  const loadStats = useCallback(async () => {
    try {
      const data = await smsService.getStatistics();
      if (data.statistics) {
        setStats({
          total_count: data.statistics.total || 0,
          sent_count: data.statistics.sent || 0,
          failed_count: data.statistics.failed || 0,
          success_rate: (data.statistics.success_rate || 0) / 100,
          by_type: data.statistics.by_type || {}
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Handle search with Enter key
  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setPage(1);
    }
  };

  // Filter notifications locally by search query
  const filteredNotifications = notifications.filter(notif => {
    const query = searchQuery.toLowerCase();
    return (
      notif.recipient_phone?.toLowerCase().includes(query) ||
      notif.recipient_name?.toLowerCase().includes(query) ||
      notif.message?.toLowerCase().includes(query)
    );
  });

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const statusConfig = {
      sent: { color: '#10b981', label: 'Sent', icon: CheckCircle },
      failed: { color: '#ef4444', label: 'Failed', icon: WarningCircle },
      pending: { color: '#f59e0b', label: 'Pending', icon: Clock },
      delivered: { color: '#06b6d4', label: 'Delivered', icon: CheckCircle }
    };
    const config = statusConfig[status] || { color: '#6b7280', label: status, icon: Smiley };
    return config;
  };

  // Get type badge
  const getTypeBadge = (type) => {
    const typeConfig = {
      fee_reminder: { bg: '#def7ff', text: '#0369a1', label: 'Fee Reminder' },
      announcement: { bg: '#fef2f2', text: '#b91c1c', label: 'Announcement' },
      attendance: { bg: '#f0fdf4', text: '#15803d', label: 'Attendance' },
      bulk: { bg: '#fef3c7', text: '#a16207', label: 'Bulk SMS' },
      general: { bg: '#f5f3ff', text: '#6d28d9', label: 'General' }
    };
    const config = typeConfig[type] || { bg: '#f3f4f6', text: '#374151', label: type };
    return config;
  };

  // Handle view notification detail
  const handleViewDetail = (notif) => {
    setSelectedNotification(notif);
    setShowDetailModal(true);
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / limit);
  const hasPrevious = page > 1;
  const hasNext = page < totalPages;

  return (
    <Layout>
      <div className="sms-dashboard">
        <div className="sms-header">
          <div className="sms-header-content">
            <h1>SMS Management</h1>
            <p>Monitor and track all SMS notifications sent through the system</p>
          </div>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="sms-stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#f3f4f6' }}>
                <Phone size={24} color="#374151" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Total SMS</p>
                <p className="stat-value">{stats.total_count || 0}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#def7ff' }}>
                <CheckCircle size={24} color="#0369a1" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Sent</p>
                <p className="stat-value">{stats.sent_count || 0}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#fef2f2' }}>
                <WarningCircle size={24} color="#b91c1c" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Failed</p>
                <p className="stat-value">{stats.failed_count || 0}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#f0fdf4' }}>
                <Smiley size={24} color="#15803d" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Success Rate</p>
                <p className="stat-value">{Math.round((stats.success_rate || 0) * 100)}%</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters Section */}
        <div className="sms-controls">
          <div className="search-box">
            <MagnifyingGlass size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search by phone, name, or message..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearch}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <select 
              value={statusFilter} 
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
              <option value="delivered">Delivered</option>
            </select>

            <select 
              value={typeFilter} 
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="filter-select"
            >
              <option value="">All Types</option>
              <option value="fee_reminder">Fee Reminder</option>
              <option value="announcement">Announcement</option>
              <option value="attendance">Attendance</option>
              <option value="bulk">Bulk SMS</option>
              <option value="general">General</option>
            </select>

            <button 
              onClick={() => {
                setPage(1);
                loadNotifications();
              }}
              className="refresh-btn"
              title="Refresh"
            >
              <ArrowsClockwise size={18} />
            </button>
          </div>
        </div>

        {/* SMS Table */}
        <div className="sms-table-container">
          <table className="sms-table">
            <thead>
              <tr>
                <th>Phone Number</th>
                <th>Recipient</th>
                <th>Message</th>
                <th>Type</th>
                <th>Status</th>
                <th>Sent At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="loading-spinner"></div>
                    Loading SMS notifications...
                  </td>
                </tr>
              ) : filteredNotifications.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    No SMS notifications found
                  </td>
                </tr>
              ) : (
                filteredNotifications.map((notif) => {
                  const statusConfig = getStatusBadge(notif.status);
                  const typeConfig = getTypeBadge(notif.notification_type);
                  return (
                    <tr key={notif.id} className="sms-row">
                      <td className="phone-cell" title={notif.recipient_phone}>
                        <Phone size={14} className="phone-icon" />
                        {notif.recipient_phone}
                      </td>
                      <td className="recipient-cell">
                        {notif.recipient_name || '-'}
                      </td>
                      <td className="message-cell" title={notif.message}>
                        {notif.message?.substring(0, 50)}
                        {notif.message?.length > 50 ? '...' : ''}
                      </td>
                      <td>
                        <span 
                          className="type-badge"
                          style={{ 
                            backgroundColor: typeConfig.bg, 
                            color: typeConfig.text 
                          }}
                        >
                          {typeConfig.label}
                        </span>
                      </td>
                      <td>
                        <span 
                          className="status-badge"
                          style={{ 
                            backgroundColor: `${statusConfig.color}20`, 
                            color: statusConfig.color,
                            borderLeft: `3px solid ${statusConfig.color}`
                          }}
                        >
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="date-cell">
                        {formatDate(notif.sent_at) || '-'}
                      </td>
                      <td>
                        <button
                          onClick={() => handleViewDetail(notif)}
                          className="action-btn"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredNotifications.length > 0 && (
          <div className="pagination">
            <button
              onClick={() => setPage(page - 1)}
              disabled={!hasPrevious}
              className="pagination-btn"
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {page} of {totalPages} ({totalCount} total)
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={!hasNext}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedNotification && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>SMS Notification Details</h2>
              <button 
                className="modal-close"
                onClick={() => setShowDetailModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <label>ID</label>
                <p>{selectedNotification.id}</p>
              </div>
              <div className="detail-row">
                <label>Phone Number</label>
                <p>{selectedNotification.recipient_phone}</p>
              </div>
              <div className="detail-row">
                <label>Recipient Name</label>
                <p>{selectedNotification.recipient_name || '-'}</p>
              </div>
              <div className="detail-row">
                <label>Message</label>
                <p className="message-detail">{selectedNotification.message}</p>
              </div>
              <div className="detail-row">
                <label>Type</label>
                <p>
                  <span 
                    className="type-badge"
                    style={{ 
                      backgroundColor: getTypeBadge(selectedNotification.notification_type).bg, 
                      color: getTypeBadge(selectedNotification.notification_type).text 
                    }}
                  >
                    {getTypeBadge(selectedNotification.notification_type).label}
                  </span>
                </p>
              </div>
              <div className="detail-row">
                <label>Status</label>
                <p>
                  <span 
                    className="status-badge"
                    style={{ 
                      backgroundColor: `${getStatusBadge(selectedNotification.status).color}20`, 
                      color: getStatusBadge(selectedNotification.status).color,
                      borderLeft: `3px solid ${getStatusBadge(selectedNotification.status).color}`
                    }}
                  >
                    {getStatusBadge(selectedNotification.status).label}
                  </span>
                </p>
              </div>
              <div className="detail-row">
                <label>Message ID</label>
                <p>{selectedNotification.message_id || '-'}</p>
              </div>
              {selectedNotification.error_message && (
                <div className="detail-row error">
                  <label>Error</label>
                  <p>{selectedNotification.error_message}</p>
                </div>
              )}
              <div className="detail-row">
                <label>Sent At</label>
                <p>{formatDate(selectedNotification.sent_at) || '-'}</p>
              </div>
              <div className="detail-row">
                <label>Created At</label>
                <p>{formatDate(selectedNotification.created_at)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default SMSDashboardPage;
